import type {PerkADto} from '@cohort/admin-schemas/perk';
import type {PerksAnalyticsOverviewADto} from '@cohort/admin-schemas/perkAnalytics';
import {Badge} from '@cohort/merchants/components/Badge';
import Button from '@cohort/merchants/components/buttons/Button';
import {PerkAppBadge} from '@cohort/merchants/components/perks/PerkAppBadge';
import {PerkTypeBadge} from '@cohort/merchants/components/perks/PerkTypeBadge';
import {PerkVisual} from '@cohort/merchants/components/PerkVisual';
import {formatDate} from '@cohort/shared/utils/format';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import {ArrowUUpLeft, List, Trash} from '@phosphor-icons/react';
import React from 'react';
import {useTranslation} from 'react-i18next';

interface PerkListItemProps {
  perk: PerkADto;
  selected?: boolean;
  handleOnSelect?: (perk: PerkADto) => void;
  handleOnRemove?: (perk: PerkADto) => void;
  handleOnRemoveCancel?: (perk: PerkADto) => void;
  isRemoved?: boolean;
  isAdded?: boolean;
}

export const PerkListItem: React.FC<PerkListItemProps> = ({
  perk,
  selected,
  handleOnSelect,
  handleOnRemove,
  handleOnRemoveCancel,
  isRemoved,
  isAdded,
}) => {
  const {t} = useTranslation('components', {keyPrefix: 'perks.perkListItem'});

  const name = getPerkName(perk);

  return (
    <div
      className={cn(
        'relative flex w-full items-center justify-between border-t-2 border-slate-100 bg-white px-2 py-4',
        isAdded && 'bg-primary/5'
      )}
    >
      <div className={cn('flex items-center space-x-4', isRemoved === true && 'opacity-30')}>
        {handleOnRemove && <List className="h-4 w-4 text-slate-400" aria-hidden="true" />}
        {handleOnSelect && (
          <input
            type="checkbox"
            data-testid="perk-list-item-checkbox"
            defaultChecked={selected ?? false}
            onChange={() => handleOnSelect(perk)}
            className="mr-2 block h-4 w-4 rounded-md text-primary shadow-sm focus:ring-primary sm:text-sm"
          />
        )}
        <div className="flex items-center space-x-4">
          <PerkVisual perk={perk} className="!h-10 !w-16" />
          <div className="flex flex-col items-start space-y-1">
            <span className="text-sm font-medium">{name}</span>
            <div className="flex items-center space-x-2">
              <PerkTypeBadge perkType={perk.type} />
              <PerkAppBadge perkIntegrationId={perk.integration?.perkIntegrationId ?? null} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <div
          className={cn('flex flex-col items-end space-y-1', isRemoved === true && 'opacity-30')}
        >
          {isAdded && (
            <Badge
              textColor="text-primary"
              backgroundColor="bg-primary/10"
              text={t('labelAdded')}
            />
          )}
          <span className="text-right text-sm text-slate-500" data-testid="last-edit">{`${t(
            'labelLastEdit'
          )} ${formatDate(perk.updatedAt)}`}</span>
        </div>
        {handleOnRemove && isRemoved !== true && (
          <Button variant="secondary" onClick={() => handleOnRemove(perk)}>
            <Trash className="h-4 w-4 text-red-500 focus:ring-transparent" size={20} />
          </Button>
        )}
        {handleOnRemoveCancel && isRemoved === true && (
          <Button variant="secondary" onClick={() => handleOnRemoveCancel(perk)}>
            <ArrowUUpLeft className="h-4 w-4 text-slate-500 focus:ring-transparent" size={20} />
          </Button>
        )}
      </div>
    </div>
  );
};

const getPerkName = (perk: PerkADto | PerksAnalyticsOverviewADto): string => {
  if ('name' in perk) {
    return perk.name;
  }
  return perk.internalName;
};
